import React, { useEffect, useState, useRef } from "react";
import { Navbar, Container, Dropdown, Image } from 'react-bootstrap';

import api from "../api";
import { useParams, useLocation, Link } from "react-router-dom";
import SocialMedia from "./SocialMedia";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShareNodes } from "@fortawesome/free-solid-svg-icons/faShareNodes";
import { faGlobe, faImages } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { FiCopy, FiCheck, FiSearch, FiImage, FiUsers } from "react-icons/fi";

import { formatDateDot } from '../utils/dateFormatter';
import { useTranslation } from "react-i18next";
import "./style/HeaderShareSheet.css";
import "./EventPublicNav.css";



const Header = ({ setEvent, social_urls, showUrls, isAuthenticated, onAlbumChange, selectedAlbum = null }) => {
  const [event, setEventState] = useState({});
  const [QR_src, setQrSrc] = useState("");
  const [loadingEid, setLoadingEid] = useState(false);
  const { id_name } = useParams();
  const location = useLocation();
  const { i18n, t } = useTranslation();

  const [albums, setAlbums] = useState([]);

  // Share sheet
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareRef = useRef(null);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    document.documentElement.dir = lng === 'he' ? 'rtl' : 'ltr';
  };

  const isAdminRoute = location.pathname.includes("admin");
  const isRTL = (i18n.language || "").startsWith("he");

  // ── Desktop tab nav ──
  const galleryPath = id_name ? `/event/${id_name}/gallery` : null;
  const isOnGallery = !!galleryPath && location.pathname === galleryPath;

  useEffect(() => {
    if (isOnGallery) sessionStorage.setItem(`gallery_visited_${id_name}`, "1");
  }, [isOnGallery, id_name]);

  const showGallery = isOnGallery || (!!id_name && !!sessionStorage.getItem(`gallery_visited_${id_name}`));

  const navTabs = id_name ? [
    { key: "find",    Icon: FiSearch, label: t("event_nav.find_me"), path: `/event/${id_name}`,         show: true },
    { key: "gallery", Icon: FiImage,  label: t("event_nav.gallery"), path: galleryPath,                show: showGallery },
    { key: "social",  Icon: FiUsers,  label: t("event_nav.social"),  path: `/event/${id_name}/social`, show: true },
  ] : [];

  const updateEvent = (eventData) => {
    setEvent(eventData);
    setEventState(eventData);
    if (eventData?.language) {
      i18n.changeLanguage(eventData.language);
      document.documentElement.dir = eventData.language === 'he' ? 'rtl' : 'ltr';
    }
  };

  // Reset direction when leaving event pages
  useEffect(() => {
    return () => { document.documentElement.dir = 'ltr'; };
  }, []);

  const fetchEventName = async () => {
    try {
      setLoadingEid(true);
      const response = await api.get(
        process.env.REACT_APP_MODEL_ENDPOINT + `/check_eid?id_name=${id_name}`
      );
      if (response.data && response.data.success) {
        updateEvent(response.data.event);
        api.post(`click/${response.data.event.event_id}`);
      } else {
        updateEvent(false);
      }
    } catch {
      updateEvent(false);
    } finally {
      setLoadingEid(false);
    }
  };

  const fetchAlbums = async (eventId) => {
    try {
      const res = await api.get(`/albums?event_id=${eventId}`, { withCredentials: true });
      setAlbums(res.data || []);
    } catch {
      setAlbums([]);
    }
  };

  const handleAlbumSelect = (album) => {
    onAlbumChange && onAlbumChange(album);
  };

  function handleCopy() {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  useEffect(() => {
    document.title = event?.name ? event.name : "Pic-loud";
    if (event.event_id) {
      setQrSrc(`${process.env.REACT_APP_MODEL_ENDPOINT}/qr?eid=${event.event_id}`);
      fetchAlbums(event.event_id);
    }
  }, [event]);

  useEffect(() => {
    fetchEventName();
  }, [id_name]);

  // Close share sheet on outside click
  useEffect(() => {
    if (!showShare) return;
    function handleOutside(e) {
      if (shareRef.current && !shareRef.current.contains(e.target)) setShowShare(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showShare]);

  return (
    <>
      <div>
        {loadingEid ? "" : event ? (
          <>
            <Navbar bg="" expand="lg" className="mb-2 py-2">
              <Container fluid className="d-flex justify-content-between align-items-center" dir="ltr">

                {/* Left: logo */}
                <div className="me-auto align-items-center">
                  <Image
                    src={`${process.env.REACT_APP_MODEL_ENDPOINT}/user_logo?user_id=${event.user?.user_id}&t=${Date.now()}` || "/logo_horizontal.png"}
                    alt="Logo"
                    height="40"
                  />
                  <span className="mx-2">
                    <strong>{event.company_name || ""}</strong>
                    {isAdminRoute && <small> Admin</small>}
                  </span>
                </div>

                {/* Right: actions */}
                <div className="ms-auto d-flex align-items-center gap-1">
                  {event && (
                    <>
                      {showUrls && <SocialMedia social_urls={social_urls} color="text-secondary" />}

                      {/* Share button + custom popover */}
                      <div className="hss-wrapper" ref={shareRef}>
                        <button
                          className="btn hss-trigger"
                          onClick={() => setShowShare((v) => !v)}
                          title="Share"
                        >
                          <FontAwesomeIcon color="grey" icon={faShareNodes} size="1x" />
                        </button>

                        {showShare && (
                          <div className="hss-sheet">
                            {/* QR code */}
                            {QR_src && (
                              <img src={QR_src} alt="QR Code" className="hss-qr" />
                            )}

                            {/* URL */}
                            <p className="hss-url">{window.location.href}</p>

                            {/* Buttons */}
                            <div className="hss-btns">
                              <button className="hss-btn" onClick={handleCopy}>
                                {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                                {copied ? "Copied!" : "Copy link"}
                              </button>
                              <a
                                className="hss-btn hss-btn--wa"
                                href={`https://wa.me/?text=${encodeURIComponent(window.location.href)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FontAwesomeIcon icon={faWhatsapp} size="sm" />
                                WhatsApp
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Albums dropdown */}
                  {albums.length > 0 && (
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        id="dropdown-albums"
                        className="rounded-pill align-items-center d-flex no-caret gap-1"
                      >
                        <FontAwesomeIcon icon={faImages} />
                        <span className="d-none d-md-inline ms-1">
                          {selectedAlbum ? selectedAlbum.name : "Albums"}
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu align="end">
                        <Dropdown.Item active={!selectedAlbum} onClick={() => handleAlbumSelect(null)}>
                          All Photos
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        {albums.map((album) => (
                          <Dropdown.Item
                            key={album.album_id}
                            active={selectedAlbum?.album_id === album.album_id}
                            onClick={() => handleAlbumSelect(album)}
                          >
                            {album.name}
                            <small className="text-muted ms-2">({album.photo_count})</small>
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}

                  {/* Language selector */}
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      size="sm"
                      id="dropdown-lng"
                      className="rounded-pill align-items-center d-flex no-caret"
                    >
                      <FontAwesomeIcon className="m-1" icon={faGlobe} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => changeLanguage("en")}>EN</Dropdown.Item>
                      <Dropdown.Item onClick={() => changeLanguage("fr")}>FR</Dropdown.Item>
                      <Dropdown.Item onClick={() => changeLanguage("he")}>HE</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Container>
            </Navbar>

            {/* Event hero / title */}
            {event.cover_image_url ? (
              <div className="event-hero" style={{ position: "relative", width: "100%", overflow: "hidden" }}>
                <img
                  src={event.cover_image_url}
                  alt={event.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 20%", display: "block" }}
                />
                <div style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(to top, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0) 55%)",
                }} />
                <div style={{
                  position: "absolute", bottom: 0, left: 0, right: 0,
                  padding: "16px 20px 20px", textAlign: "center",
                }}>
                  <h2 style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "clamp(1.5rem, 4.5vw, 2.8rem)",
                    fontWeight: 400, letterSpacing: "0.13em",
                    color: "#fff", textTransform: "uppercase",
                    margin: 0, lineHeight: 1.1,
                    textShadow: "0 1px 6px rgba(0,0,0,0.35)",
                  }}>
                    {event.name}
                  </h2>
                  {(event.date || event.location) && (
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 8,
                      marginTop: 7, fontSize: "0.6rem", fontWeight: 400,
                      color: "rgba(255,255,255,0.78)", letterSpacing: isRTL ? "0" : "0.3em", textTransform: "uppercase",
                      padding: "0 12px",
                    }}>
                      {event.date && <span>{formatDateDot(event.date)}</span>}
                      {event.date && event.location && (
                        <span style={{ width: 2.5, height: 2.5, borderRadius: "50%", background: "rgba(255,255,255,0.55)", display: "inline-block" }} />
                      )}
                      {event.location && <span>{event.location}</span>}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                padding: "36px 24px 24px", textAlign: "center",
              }}>
                <h2
                  className="fade-in"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "clamp(2rem, 5.5vw, 3.8rem)",
                    fontWeight: 400, letterSpacing: "0.13em",
                    color: "#111", textTransform: "uppercase",
                    margin: 0, lineHeight: 1.0,
                  }}
                >
                  {event.name}
                </h2>
                {(event.date || event.location) && (
                  <>
                    <div style={{ width: 44, height: "0.5px", background: "#c9c9c9", margin: "18px auto 15px" }} />
                    <div style={{
                      display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "center", gap: 10,
                      fontSize: "0.6rem", fontWeight: 400,
                      color: "#aaa", letterSpacing: isRTL ? "0" : "0.3em", textTransform: "uppercase",
                      padding: "0 12px",
                    }}>
                      {event.date && <span>{formatDateDot(event.date)}</span>}
                      {event.date && event.location && (
                        <span style={{ width: 2.5, height: 2.5, borderRadius: "50%", background: "#ccc", display: "inline-block" }} />
                      )}
                      {event.location && <span>{event.location}</span>}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Desktop tab nav — hidden on mobile (uses bottom bar instead) ── */}
            {navTabs.length > 0 && (
              <div
                className="d-none d-md-flex justify-content-center"
                style={{ borderTop: "1px solid #f0f0f0", marginTop: 8 }}
              >
                {navTabs.filter((tab) => tab.show).map(({ key, Icon, label, path }) => {
                  const active = location.pathname === path;
                  return (
                    <Link
                      key={key}
                      to={path}
                      className={`event-public-nav__tab${active ? " event-public-nav__tab--active" : ""}`}
                      aria-current={active ? "page" : undefined}
                    >
                      <Icon size={18} aria-hidden="true" className="event-public-nav__icon" />
                      <span className="event-public-nav__label">{label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <h2 className="text-center">
            <img src="/logo_horizontal.png" alt="Pic-loud" style={{ width: "auto", height: "25px" }} />
          </h2>
        )}
      </div>
    </>
  );
};

export default Header;
